import { useState } from 'react';

// Helper function to render bio with highlighted text
const renderBioWithHighlights = (bioText) => {
  // Split by <strong class="orange-highlight"> and </strong> tags
  const parts = bioText.split(/(<strong class="orange-highlight">.*?<\/strong>)/g);
  
  return parts.map((part, index) => {
    if (part.startsWith('<strong class="orange-highlight">')) {
      const text = part.replace(/<strong class="orange-highlight">|<\/strong>/g, '');
      return (
        <strong key={index} className="text-[#9a83ad] font-semibold">
          {text}
        </strong>
      );
    }
    return <span key={index}>{part}</span>;
  });
};

const teamMembers = [
  {
    id: 1,
    name: "Amber Valdez",
    role: "Chief Executive Officer",
    bio: "Amber Valdez serves as Chief Executive Officer, overseeing the company's day-to-day operations with a steadfast commitment to accuracy, reliability, and quality.",
    bioFull: "Amber Valdez serves as Chief Executive Officer, overseeing the company's day-to-day operations with a steadfast commitment to accuracy, reliability, and quality. With a deep understanding of the political & non-profit communications space, Amber plays a hands-on role in ensuring every campaign is executed thoughtfully, efficiently, and with the highest level of care. Her leadership style blends precision with consistency — setting clear expectations, maintaining operational excellence, and making sure both clients and internal teams can rely on seamless execution from start to finish. Amber is instrumental in keeping complex projects moving smoothly while upholding the standards that define the company's reputation.",
    email: "Amber@thevbcgroup.com",
    phone: "(817) 528-5471",
    side: "left"
  },
  {
    id: 2,
    name: "Rachele Christian",
    role: "Chief Sales Officer",
    bio: "Rachele Christian serves as Chief Sales Officer, specializing in strategic partnerships, client relationships, and cross-team coordination.",
    bioFull: "Rachele Christian serves as Chief Sales Officer, specializing in strategic partnerships, client relationships, and cross-team coordination. Rachele works closely with clients and internal teams to ensure solutions are thoughtfully designed, clearly communicated, and successfully executed. She plays a key role in aligning sales strategy with operational delivery — acting as a bridge between client goals and the teams responsible for bringing campaigns to life. Known for her collaborative leadership style and attention to detail, Rachele is passionate about building long-term partnerships rooted in trust, transparency, and results. Whether supporting consultants, campaign managers, or internal teams, Rachele focuses on creating smart, efficient outreach plans that set clients up for success while keeping communication seamless every step of the way.",
    email: "Rachele@thevbcgroup.com",
    phone: "(682) 472-2638",
    side: "right"
  },
  {
    id: 3,
    name: "Jeff Fournier",
    role: "Senior Account Executive",
    bio: "Jeff Fournier serves as Senior Account Executive, managing strategic partnerships, high-volume clients, and complex enterprise relationships.",
    bioFull: "Jeff Fournier serves as Senior Account Executive, managing strategic partnerships, high-volume clients, and complex enterprise relationships. He works closely with resellers, consultants, and organizations that require customized outreach solutions, serving as their primary point of contact from initial planning through execution. With extensive experience handling large-scale programs and nuanced client needs, Jeff is known for his ability to translate goals into effective, scalable strategies. His consultative approach helps partners navigate campaign logistics, timelines, and technical considerations while ensuring each solution is tailored to meet specific objectives. Jeff's focus on responsiveness, reliability, and long-term relationship building makes him a trusted advisor to clients who rely on precision, flexibility, and proven results.",
    email: "Jeff@thevbcgroup.com",
    phone: "(214) 244-2255",
    side: "left"
  },
  {
    id: 4,
    name: "Ben Sargent",
    role: "Executive Account Manager",
    bio: "Ben Sargent serves as Executive Account Manager, focused on ensuring client campaigns are delivered accurately, efficiently, and on schedule.",
    bioFull: "Ben Sargent serves as Executive Account Manager, focused on ensuring client campaigns are delivered accurately, efficiently, and on schedule. He works closely with both clients and internal teams to manage campaign details, timelines, and execution — helping turn strategy into results. Ben is known for his hands-on approach and attention to detail, proactively monitoring each campaign to ensure requirements are met and expectations are exceeded. His commitment to precision and follow-through helps clients feel confident that their outreach is in capable hands from start to finish. By prioritizing clear communication and dependable execution, Ben plays a key role in maintaining the consistency and quality that define the company's client experience.",
    email: "Ben@thevbcgroup.com",
    phone: "(817) 449-9700",
    side: "right"
  }
];

const Team = () => {
  const [expandedCard, setExpandedCard] = useState(null);

  const handleSeeMore = (id) => {
    setExpandedCard(id);
    setTimeout(() => {
      const card = document.getElementById(`team-card-${id}`);
      if (card) {
        card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);
  };

  const handleSeeLess = (id) => {
    setExpandedCard(null);
    setTimeout(() => {
      const card = document.getElementById(`team-card-${id}`);
      if (card) {
        card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);
  };

  return (
    <section
      id="team"
      className="py-12 md:py-16 lg:py-24 px-4 sm:px-6 lg:px-8 m-0 bg-[var(--bg-charcoal)] relative overflow-x-hidden z-[4] w-full"
    >
      <div className="!max-w-[1520px] mx-auto w-full overflow-hidden">
        {/* Section Title */}
        <h2 className="text-center text-3xl md:text-4xl lg:text-5xl mb-8 md:mb-12 lg:mb-16 text-[var(--bg-pure-white)] relative block w-full font-semibold uppercase tracking-[4px] font-serif border-b border-white/15 pb-6 after:content-[''] after:absolute after:-bottom-[10px] after:left-0 after:w-[250px] after:h-[6px] after:bg-[#9a83ad]">
          Meet Our Team
        </h2>

        {/* Team List - Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 mt-0 relative z-[1] justify-items-stretch items-stretch w-full">
          {teamMembers.map((member) => {
            const isExpanded = expandedCard === member.id;
            const isLeft = member.side === 'left';

            return (
              <div
                key={member.id}
                id={`team-card-${member.id}`}
                className={`
                  bg-[var(--bg-light)] p-0 rounded-[2px] border border-[var(--border-light)]
                  transition-all duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)] w-full max-w-full min-h-full
                  flex flex-row gap-0 items-stretch
                  shadow-[0_4px_6px_rgba(0,0,0,0.07),0_2px_4px_rgba(0,0,0,0.05)] relative overflow-hidden z-[1]
                  ${isExpanded ? 'md:col-span-2 relative z-[10]' : ''}
                  ${isLeft ? 'self-start' : 'self-end'}
                  hover:-translate-y-2 hover:shadow-[0_20px_40px_rgba(0,0,0,0.12),0_8px_16px_rgba(0,0,0,0.08)]
                `}
              >
                {/* Team Card Content */}
                <div className="flex flex-col flex-[1.75] w-[62.5%] text-left bg-[var(--bg-light)] p-6 md:p-8 transition-colors duration-300 justify-start">
                  {/* Team Name */}
                  <h3 className="text-xl md:text-2xl mb-1 mt-0 text-[var(--bg-darker)] font-bold uppercase tracking-[1.5px] leading-[1.2]">
                    {member.name}
                  </h3>

                  {/* Team Role */}
                  <p className="text-[var(--text-medium)] font-semibold mb-0 text-sm md:text-base uppercase tracking-[1px] font-normal text-left">
                    {member.role}
                  </p>

                  {/* Team Bio Wrapper */}
                  <div className="mt-4">
                    {/* Short Bio */}
                    {!isExpanded && (
                      <p className="text-[var(--text-medium)] leading-[1.6] text-sm md:text-base mb-3 text-left block">
                        {member.bio}
                      </p>
                    )}

                    {/* Full Bio */}
                    {isExpanded && (
                      <p className="text-[var(--text-medium)] leading-[1.6] text-sm md:text-base mb-0 mt-0 text-left block">
                        {renderBioWithHighlights(member.bioFull)}
                      </p>
                    )}

                    {/* See More Button */}
                    {!isExpanded && (
                      <button
                        onClick={() => handleSeeMore(member.id)}
                        className="inline-block mt-3 py-2 px-5 bg-transparent text-[#9a83ad] border border-[#9a83ad] rounded-none text-sm md:text-base font-semibold uppercase tracking-[1px] cursor-pointer transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] font-serif hover:bg-[#9a83ad] hover:text-white hover:-translate-y-0.5 hover:shadow-[0_4px_8px_rgba(154,131,173,0.3)]"
                      >
                        Show More
                      </button>
                    )}

                    {/* See Less Button */}
                    {isExpanded && (
                      <button
                        onClick={() => handleSeeLess(member.id)}
                        className="inline-block mt-3 py-2 px-5 bg-transparent text-[#9a83ad] border border-[#9a83ad] rounded-none text-sm md:text-base font-semibold uppercase tracking-[1px] cursor-pointer transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] font-serif hover:bg-[#9a83ad] hover:text-white hover:-translate-y-0.5 hover:shadow-[0_4px_8px_rgba(154,131,173,0.3)]"
                      >
                        Show Less
                      </button>
                    )}
                  </div>
                </div>

                {/* Team Contact Section */}
                <div className={`
                  flex flex-col gap-6 items-start justify-center p-7 bg-[var(--bg-dark)] flex-[1.25] w-[37.5%]
                  border-t-0 border-l border-l-white/10
                  ${!isLeft ? 'border-r-0' : ''}
                `}>
                  {/* Email Link */}
                  <a
                    href={`mailto:${member.email}`}
                    className="text-white no-underline text-sm md:text-base transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] flex items-center gap-3 p-0 leading-[1.5] w-full hover:text-[#9a83ad] group"
                  >
                    <svg
                      className="flex-shrink-0 w-7 h-7 opacity-100 p-1.5 bg-[var(--text-dark)] rounded-full stroke-[#9a83ad] fill-none box-border transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:scale-110"
                      viewBox="0 0 24 24"
                      fill="none"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                    <span className="inline-block transition-colors duration-300">{member.email}</span>
                  </a>

                  {/* Phone Link */}
                  <a
                    href={`tel:${member.phone.replace(/\D/g, '')}`}
                    className="text-white no-underline text-sm md:text-base transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] flex items-center gap-3 p-0 leading-[1.5] w-full hover:text-[#9a83ad] group"
                  >
                    <svg
                      className="flex-shrink-0 w-7 h-7 opacity-100 p-1.5 bg-[var(--text-dark)] rounded-full stroke-[#9a83ad] fill-none box-border transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:scale-110"
                      viewBox="0 0 24 24"
                      fill="none"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                    <span className="inline-block transition-colors duration-300">{member.phone}</span>
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Team;

